import React, { useState, useRef, useLayoutEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { defer, debounce } from 'lodash/fp';

import { PlatformDetails } from '../../../../common/platform/platformTypes';

import { getPlatformDetailsSelector } from '../../../platform/platformSelector';

import { isMobileMode, isPlatformTablet } from '../../../platform/utils/platformDetailsUtils';
import { getTemplateId, getTemplateIsCustom, matchTemplate } from '../boardTemplatePickerUtil';

import { sendAmplitudeEvent } from '../../../analytics/amplitudeService';

import Icon from '../../../components/icons/Icon';
import SearchClearButton from '../../../components/form/SearchClearButton';

import CategoryButton from './BoardTemplatePickerBodyListingCategoryButton';
import TemplateButton from './BoardTemplatePickerBodyListingTemplateButton';
import { boardTemplatePickerContext } from '../context/boardTemplatePickerContext';

import { Template, TemplateCollection, TemplatesMap } from '../templateTypes';

import './BoardTemplatePickerBodyCategoriesList.scss';

const TemplateSearchResults = ({
    searchResults,
    selectedTemplateId,
    selectTemplate,
    templates,
}: {
    searchResults: Template[];
    selectedTemplateId: string | null;
    selectTemplate: (templateId: string | null) => void;
    templates: TemplatesMap;
}) => {
    if (searchResults.length === 0) {
        return (
            <div className="picker-templates">
                <div className="no-results">No templates found</div>
            </div>
        );
    }

    return (
        <ul className="picker-templates">
            {searchResults.map((template) => (
                <li key={getTemplateId(template)}>
                    <TemplateButton
                        templateEntry={template}
                        templates={templates}
                        isCustomTemplate={getTemplateIsCustom(template)}
                        selectedTemplateId={selectedTemplateId}
                        selectTemplate={selectTemplate}
                    />
                </li>
            ))}
        </ul>
    );
};

const trackSearchAnalytics = debounce(2000, (searchTerm) =>
    sendAmplitudeEvent({
        eventType: 'Searched templates',
        eventProperties: {
            searchTerm,
        },
    }),
);

type Props = {
    className?: string;
    templateCollections: TemplateCollection[];
};

const BoardTemplatePickerCategoriesList = ({ className, templateCollections }: Props) => {
    const { templates, selectedTemplateId, selectTemplate, setNavigation, categoriesLastScrollTopRef } =
        useContext(boardTemplatePickerContext);

    const [search, setSearch] = useState('');
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const pickerBodyRef = useRef<HTMLDivElement | null>(null);
    const platformDetails = useSelector(getPlatformDetailsSelector);

    const focusSearchInput = () => searchInputRef.current && searchInputRef.current.focus();

    const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => event.preventDefault();

    const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = event.target.value;
        setSearch(searchTerm);
        trackSearchAnalytics(searchTerm);
    };

    const handleSearchInputClear = () => {
        setSearch('');
        defer(focusSearchInput);
    };

    const shouldFocusSearchInput = (platformDetails: PlatformDetails) =>
        !(isPlatformTablet(platformDetails) || isMobileMode(platformDetails));

    useLayoutEffect(() => {
        // wait until transition is complete before focusing search input,
        // otherwise, safari tries to scroll to the input and it breaks the transition
        if (shouldFocusSearchInput(platformDetails)) {
            setTimeout(() => {
                focusSearchInput();
                if (!pickerBodyRef.current) return;
                pickerBodyRef.current.scrollTop = categoriesLastScrollTopRef.current;
            }, 400);
        }

        // on mount, scroll to the previous scroll position
        if (pickerBodyRef.current) {
            pickerBodyRef.current.scrollTop = categoriesLastScrollTopRef.current;
        }

        // on unmount, save the scroll position of the category list
        return () => {
            if (pickerBodyRef.current) {
                categoriesLastScrollTopRef.current = pickerBodyRef.current.scrollTop;
            }
        };
    }, []);

    const hasSearch = search.length > 0;
    const searchResults = Object.values(templates).filter(matchTemplate(search));
    const displayedList = hasSearch ? searchResults : templateCollections;

    return (
        <div
            ref={pickerBodyRef}
            className={classNames('BoardTemplatePickerBodyCategoriesList', className, {
                scrollable: displayedList.length > 6,
            })}
            data-scrollable={true}
        >
            <form className="search-form" onSubmit={handleSearchSubmit}>
                <input
                    ref={searchInputRef}
                    className="FormInput"
                    placeholder="Search templates..."
                    value={search}
                    onChange={handleSearchInputChange}
                />
                <SearchClearButton
                    show
                    canClear={!!search.length}
                    onMouseDown={handleSearchInputClear}
                    onClick={handleSearchInputClear}
                />
            </form>

            {hasSearch ? (
                <TemplateSearchResults
                    selectedTemplateId={selectedTemplateId}
                    selectTemplate={selectTemplate}
                    searchResults={searchResults}
                    templates={templates}
                />
            ) : (
                <ul className="picker-categories">
                    {templateCollections.map((category) => (
                        <li key={category.id}>
                            <CategoryButton categoryId={category.id} setNavigation={setNavigation}>
                                {category.title}

                                <span className="count">{category.templates.length}</span>

                                <Icon name="chevron-left" />
                            </CategoryButton>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default BoardTemplatePickerCategoriesList;
